<template>
  <div
    class="secure-pdf-wrap"
    ref="wrap"
    @contextmenu.prevent
    @dragstart.prevent
    @copy.prevent
    @cut.prevent>

    <div v-if="!resourceId" class="secure-pdf-wrap__empty">
      <p>No document provided.</p>
    </div>

    <template v-else>
      <!-- Toolbar: hides download/print unless accessMode allows -->
      <div class="secure-pdf-toolbar">
        <span class="secure-pdf-toolbar__title">{{ title || 'Document' }}</span>
        <div class="secure-pdf-toolbar__right">
          <button
            v-if="accessMode === 'download_allowed'"
            class="secure-pdf-toolbar__btn"
            @click="download"
            :disabled="!pdfBytes"
            :aria-label="'Download ' + (title || 'document')">
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z" /><path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" /></svg>
            Download
          </button>
          <span class="secure-pdf-toolbar__watermark-note" v-if="accessMode === 'view_only'">
            View only — download &amp; print disabled
          </span>
        </div>
      </div>

      <!-- Scrollable document surface. Pages render to <canvas> (no text
           layer), so right-click Save/Print, text selection and copy are all
           genuinely suppressible here — the browser's native PDF plugin is
           never loaded. -->
      <div class="secure-pdf-doc" ref="doc">
        <div v-if="loading" class="secure-pdf-status">
          <span class="secure-pdf-spinner"></span>
          <span>Preparing protected document…</span>
        </div>
        <div v-else-if="error" class="secure-pdf-status secure-pdf-status--error">
          {{ error }}
        </div>

        <div class="secure-pdf-pages" ref="pages" v-show="!loading && !error">
          <!-- Watermark overlay — tiled SVG so it covers every page no matter
               how tall the document scrolls. Click-through (pointer-events:none). -->
          <div class="secure-pdf-watermark" :style="watermarkStyle" aria-hidden="true"></div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
// Side-effect import: registers window.pdfjsWorker so pdf.js runs the worker
// from the bundle instead of fetching a separate file (webpack-4 friendly).
import 'pdfjs-dist/legacy/build/pdf.worker.entry';

export default {
  name: 'SecurePDFViewer',
  props: {
    /** tbl_course_resources id — bytes are streamed via /glad/secure-asset/stream */
    resourceId: { type: [Number, String], default: null },
    /** 'view_only' | 'download_allowed' */
    accessMode: { type: String, default: 'view_only' },
    /** Logged-in user's email — burned into watermark */
    userEmail: { type: String, default: '' },
    /** Human-readable document title */
    title: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      error: '',
      pdfBytes: null,   // ArrayBuffer kept for the download_allowed path
      // NOTE: keep these names underscore-free — Vue 2 does NOT proxy data keys
      // that start with `_`/`$`, so `this.renderToken` would be undefined and
      // the stale-render guards would silently break.
      renderToken: 0,   // bumps on reload/unmount to abort stale renders
    };
  },
  created() {
    // Held off the reactive data bag on purpose: Vue should not deep-observe the
    // pdf.js document object.
    this.pdfDoc = null;
  },
  computed: {
    watermarkText() {
      const date = new Date().toISOString().split('T')[0];
      return `${this.userEmail}  ·  ${date}  ·  Confidential`;
    },
    watermarkStyle() {
      // Tiled diagonal SVG stamp, built as a data URI so it repeats across the
      // full scroll height of the document.
      const text = this.watermarkText.replace(/[<>&]/g, ' ');
      const svg =
        `<svg xmlns='http://www.w3.org/2000/svg' width='360' height='210'>` +
        `<text x='50%' y='50%' fill='rgba(15,17,21,0.13)' font-size='13' ` +
        `font-family='sans-serif' text-anchor='middle' ` +
        `transform='rotate(-30 180 105)'>${text}</text></svg>`;
      return {
        backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`,
        backgroundRepeat: 'repeat',
      };
    },
  },
  watch: {
    resourceId() {
      this.load();
    },
  },
  mounted() {
    // Block Save (Ctrl/Cmd+S) and Print (Ctrl/Cmd+P) while the secure viewer
    // is on screen — these bypass right-click but reach the same outcome.
    window.addEventListener('keydown', this.blockSaveAndPrint, true);
    this.load();
  },
  beforeDestroy() {
    this.renderToken++;
    window.removeEventListener('keydown', this.blockSaveAndPrint, true);
    if (this.pdfDoc) {
      try { this.pdfDoc.destroy(); } catch (e) { /* noop */ }
      this.pdfDoc = null;
    }
  },
  methods: {
    blockSaveAndPrint(e) {
      const k = (e.key || '').toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (k === 's' || k === 'p')) {
        e.preventDefault();
        e.stopPropagation();
      }
    },

    async load() {
      if (!this.resourceId) return;
      const token = ++this.renderToken;
      this.loading = true;
      this.error = '';
      this.pdfBytes = null;
      this.clearPages();

      try {
        const resp = await this.$http.get('glad/secure-asset/stream', {
          params: { resource_id: this.resourceId },
          responseType: 'arraybuffer',
        });
        if (token !== this.renderToken) return; // superseded

        this.pdfBytes = resp.data;
        // pdf.js consumes (and detaches) the buffer it's given, so hand it a copy
        // and keep the original for the download path.
        const data = new Uint8Array(resp.data.slice(0));
        const task = pdfjsLib.getDocument({ data, disableAutoFetch: true, disableStream: true });
        const pdf = await task.promise;
        if (token !== this.renderToken) { try { pdf.destroy(); } catch (e) {} return; }

        this.pdfDoc = pdf;
        await this.renderAll(pdf, token);
      } catch (err) {
        if (token !== this.renderToken) return;
        const status = err && err.response && err.response.status;
        this.error = status === 403
          ? 'You do not have access to this document.'
          : 'This document could not be loaded.';
      } finally {
        if (token === this.renderToken) this.loading = false;
      }
    },

    clearPages() {
      // Remove only the rendered page canvases — NOT innerHTML, which would
      // also destroy the Vue-managed watermark overlay that lives in here.
      const host = this.$refs.pages;
      if (host) host.querySelectorAll('canvas.secure-pdf-page').forEach(c => c.remove());
    },

    async renderAll(pdf, token) {
      const host = this.$refs.pages;
      const doc = this.$refs.doc;
      if (!host || !doc) return;

      // Fit page width to the available surface, scaled up for crisp rendering
      // on high-DPI displays.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const available = Math.max(doc.clientWidth - 32, 280);

      for (let n = 1; n <= pdf.numPages; n++) {
        if (token !== this.renderToken) return;
        const page = await pdf.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const scale = available / base.width;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.className = 'secure-pdf-page';
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = Math.floor(viewport.width) + 'px';
        canvas.style.height = Math.floor(viewport.height) + 'px';
        // Belt-and-braces: even the canvas image is not draggable / selectable.
        canvas.setAttribute('draggable', 'false');

        const ctx = canvas.getContext('2d');
        host.appendChild(canvas);

        await page.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null,
        }).promise;

        if (token !== this.renderToken) return;
      }
    },

    download() {
      if (this.accessMode !== 'download_allowed' || !this.pdfBytes) return;
      const blob = new Blob([this.pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (this.title || 'document').replace(/[^A-Za-z0-9._-]/g, '_') + '.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.secure-pdf-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 82vh;
  min-height: 560px;
  background: var(--glad-paper-2);
  border-radius: var(--glad-radius);
  overflow: hidden;
  font-family: var(--glad-font-sans);
  /* No text layer is rendered, but guard selection on the chrome too. */
  user-select: none;
  -webkit-user-select: none;
}

.secure-pdf-wrap__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--glad-text-soft);
  font-size: 0.9rem;
}

.secure-pdf-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1rem;
  background: var(--glad-ink);
  color: #fff;
  flex-shrink: 0;
}
.secure-pdf-toolbar__title {
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 55%;
}
.secure-pdf-toolbar__right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.secure-pdf-toolbar__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.25);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.3rem 0.7rem;
  border-radius: 6px;
  cursor: pointer;
  font-family: var(--glad-font-sans);
  &:hover { background: rgba(255,255,255,0.25); }
  &:disabled { opacity: 0.5; cursor: default; }
}
.secure-pdf-toolbar__watermark-note {
  font-size: 0.72rem;
  color: rgba(255,255,255,0.6);
}

.secure-pdf-doc {
  position: relative;
  flex: 1;
  overflow: auto;
  background: #525659; /* neutral reader backdrop, like a PDF viewer */
}

.secure-pdf-pages {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px;
  min-height: 100%;
}

::v-deep .secure-pdf-page {
  display: block;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
  background: #fff;
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
  pointer-events: none; /* the page image itself is inert */
}

.secure-pdf-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  height: 100%;
  color: rgba(255,255,255,0.85);
  font-size: 0.85rem;
}
.secure-pdf-status--error { color: #ffd9d9; }

.secure-pdf-spinner {
  width: 22px;
  height: 22px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: secure-pdf-spin 0.8s linear infinite;
}
@keyframes secure-pdf-spin { to { transform: rotate(360deg); } }

.secure-pdf-watermark {
  position: absolute;
  inset: 0;
  z-index: 10;
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
}
</style>
